// src/UserBadge.jsx
import React, { useEffect, useRef, useState } from "react";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth } from "./firebase/firebaseConfig";
import { useAuth } from "./firebase/AuthContext";
import { API_BASE } from "./api/config";
import InstallPWAButton from "./InstallPWAButton";
import SettingsDrawer from "./SettingsDrawer";
import AboutModal from "./AboutModal";
import { getDiscordDestinationAction, getDiscordStatus } from "./api/discord";
import { discordActionLabel } from "./discordPresentation";

// --- optional in-app help (POST) with graceful mailto fallback ---
async function myHelpFunction() {
  try {
    const res = await fetch(`${API_BASE}/support/contact`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        subject: "Help from in-app",
        message: "User opened Help (quick tap).",
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
    alert("Message sent. We’ll reply by email.");
  } catch {
    const to = "playswithguru@gmail.com";
    const subject = encodeURIComponent("Support request");
    const body = encodeURIComponent("Hi team,\n\nI need help with ...\n\nThanks!");
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  }
}

// ---- Theme tokens
const BRAND = {
  light: { primary: "#0095e0", text: "#1F2A44", bg: "#FFFFFF", border: "#0095e0", menuBg: "#FFFFFF", menuDivider: "#E5E7EB" },
  dark:  { primary: "#7ED3FF", text: "#E6ECFF", bg: "#121212", border: "#7ED3FF", menuBg: "#0F172A", menuDivider: "#334155" },
};
const NEUTRAL = {
  light: { primary: "#111827", text: "#111827", bg: "#FFFFFF", border: "#C7CDD6", menuBg: "#FFFFFF", menuDivider: "#E5E7EB" },
  dark:  { primary: "#E6ECFF", text: "#E6ECFF", bg: "#121212", border: "#374151", menuBg: "#0F172A", menuDivider: "#334155" },
};
const BORDER_WIDTH = 1.5;

export default function UserBadge({
  darkMode = false,
  tone = "brand",
  compact = false,

  // from PropsDashboard
  isSubscribed = false,
  isAdmin = false,
  isStripeSubscriber = false,
  subscriptionLoading = false,
  onSubscribe,
  onOpenBilling,
  onLogin,
  onLogout,
  onOpenHelp,
}) {
  const { user, userDoc, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const [discordMembership, setDiscordMembership] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);

  const wrapRef = useRef(null);
  const navigate = useNavigate();
  if (loading) return null;

  // Optional: let the header logo open About
  useEffect(() => {
    const openAbout = () => setAboutOpen(true);
    window.addEventListener("pwg:open-about", openAbout);
    return () => window.removeEventListener("pwg:open-about", openAbout);
  }, []);

  useEffect(() => {
    let active = true;
    if (!user) {
      setDiscordMembership(null);
      return () => { active = false; };
    }
    getDiscordStatus()
      .then((status) => { if (active) setDiscordMembership(status); })
      .catch(() => { if (active) setDiscordMembership(null); });
    return () => { active = false; };
  }, [user]);

  const pal =
    tone === "neutral"
      ? (darkMode ? NEUTRAL.dark : NEUTRAL.light)
      : (darkMode ? BRAND.dark : BRAND.light);

  const name = user
    ? (user.displayName || (user.email ? user.email.split("@")[0] : "User"))
    : "Guest";
  const email = user?.email;
  const avatar = user?.photoURL;
  const initials = (name?.[0] || "G").toUpperCase();

  useEffect(() => {
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const capsuleStyle = {
    display: "flex", alignItems: "center", gap: "0.5rem",
    padding: compact ? 5 : "0.50rem 0.85rem 0.50rem 0.50rem",
    width: compact ? 40 : undefined,
    height: compact ? 40 : undefined,
    justifyContent: compact ? "center" : undefined,
    borderRadius: 9999, cursor: "pointer", fontWeight: 700,
    backgroundColor: pal.bg, color: pal.primary,
    border: `${BORDER_WIDTH}px solid ${pal.border}`,
    outline: "none", boxShadow: "0 1px 2px rgba(0,0,0,.06)",
  };
  const nameStyle = { maxWidth: "12ch", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", paddingRight: 2 };
  const menuStyle = {
    position: "absolute",
    right: 0,
    top: "calc(100% + 10px)",
    width: 260,
    backgroundColor: pal.menuBg,
    color: pal.text,
    border: `1px solid ${pal.menuDivider}`,
    borderRadius: 12,
    boxShadow: "0 20px 40px rgba(0,0,0,.15)",
    overflow: "hidden",
    zIndex: 10000, // ✅ was 40
  };
  
  const itemStyle = { width: "100%", textAlign: "left", padding: "0.7rem 1rem", background: "transparent", border: "none", cursor: "pointer", fontWeight: 600 };

  const closeAnd = (fn) => async () => { try { await fn?.(); } finally { setOpen(false); } };

  const handleLogout = closeAnd(async () => {
    if (!user) return;
    await signOut(auth);
    try { onLogout?.(); } catch {}
    navigate("/login", { replace: true });
  });

  const handleLogin = closeAnd(() => { if (onLogin) onLogin(); else navigate("/login"); });

  const handleBilling = closeAnd(async () => {
    if (!user) { handleLogin(); return; }
    try {
      if (onOpenBilling) await onOpenBilling();
      else navigate("/billing");
    } catch (e) {
      console.error(e);
      alert("Could not open billing. Please try again.");
    }
  });

  const handleSubscribe = closeAnd(async () => {
    if (!user) { handleLogin(); return; }
    try {
      if (onSubscribe) await onSubscribe();
      else navigate("/subscribe");
    } catch (e) {
      console.error(e);
      alert("Could not start subscription. Please try again.");
    }
  });

  const handleDiscord = closeAnd(async () => {
    try {
      const result = await getDiscordDestinationAction();
      if (!result?.url) throw new Error("Discord destination was not returned.");
      window.location.assign(result.url);
    } catch (error) {
      console.error("Discord connection failed:", error);
      alert(error?.message || "Unable to connect Discord.");
    }
  });

  return (
    <div
      ref={wrapRef}
      style={{ position: "relative", zIndex: open ? 10000 : "auto" }}
    >
  
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={user ? "Open account menu" : "Sign in or open account menu"}
        className="pwgCompactAccountControl"
        style={capsuleStyle}
      >
        {avatar && !avatarFailed ? (
          <img src={avatar} alt="" onError={() => setAvatarFailed(true)} style={{ width: 28, height: 28, borderRadius: "9999px", objectFit: "cover" }} />
        ) : (
          <div
            style={{
              width: 28, height: 28, borderRadius: "9999px",
              backgroundColor: pal.primary, color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 14, fontWeight: 700,
            }}
          >
            {initials}
          </div>
        )}
        {!compact && <span style={nameStyle}>{name}</span>}
        {!compact && <svg
          width="14" height="14" viewBox="0 0 20 20"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .15s ease", opacity: 0.85 }}
          aria-hidden="true"
        >
          <path d="M5.5 7.5l4.5 4.5 4.5-4.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>}
      </button>

      {open && (
        <div role="menu" style={menuStyle}>
          <div style={{ padding: "0.85rem 1rem" }}>
            <div style={{ fontWeight: 800, lineHeight: 1.1 }}>{name}</div>
            {email && <div style={{ fontSize: 12, opacity: 0.7 }}>{email}</div>}
          </div>

          <div style={{ height: 1, background: pal.menuDivider }} />

          {user ? (
            <>
              {isAdmin ? (
                <div style={{ ...itemStyle, cursor: "default", color: pal.primary }}>Admin Access</div>
              ) : !isSubscribed ? (
                <button role="menuitem" type="button" onClick={handleSubscribe} style={itemStyle} disabled={subscriptionLoading}>
                  {subscriptionLoading ? "Starting…" : "Subscribe"}
                </button>
              ) : isStripeSubscriber ? (
              <button role="menuitem" type="button" onClick={handleBilling} style={itemStyle}>
                Manage Subscription
              </button>
              ) : (
                <div style={{ ...itemStyle, cursor: "default", color: pal.primary }}>Premium Access</div>
              )}

              <button role="menuitem" onClick={closeAnd(() => setAboutOpen(true))} style={itemStyle}>
                About PlaysWithGuru
              </button>

              <button role="menuitem" onClick={closeAnd(() => setSettingsOpen(true))} style={itemStyle}>
                App Settings
              </button>

              <button role="menuitem" onClick={handleDiscord} style={itemStyle}>
                {discordActionLabel(discordMembership)}
              </button>

              <div style={{ padding: "0 1rem 0.25rem" }}>
                <InstallPWAButton className="btn-secondary" />
              </div>

              <button role="menuitem" onClick={closeAnd(myHelpFunction)} style={itemStyle}>
                Help & Support
              </button>

              <div style={{ height: 1, background: pal.menuDivider }} />

              <button role="menuitem" onClick={handleLogout} style={itemStyle}>
                Logout
              </button>
            </>
          ) : (
            <>
              <button role="menuitem" onClick={handleLogin} style={itemStyle}>
                Login
              </button>
              <button role="menuitem" onClick={closeAnd(() => setAboutOpen(true))} style={itemStyle}>
                About PlaysWithGuru
              </button>
              <button role="menuitem" onClick={closeAnd(myHelpFunction)} style={itemStyle}>
                Help & Support
              </button>
            </>
          )}
        </div>
      )}

      {settingsOpen && (
        <SettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      )}

      {aboutOpen && <AboutModal open={aboutOpen} onClose={() => setAboutOpen(false)} />}
    </div>
  );
}
